import { createSelector } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'data';
import { TemplateId } from 'data/templates/api';
import { selectActiveTemplateId } from '../selectors';
import { ProjectTemplate, ProjectTemplateStoreRecord } from '../types';

const getActiveProjectTemplate = () =>
  createSelector(
    (state: RootState) => state.templates.project,
    (_, templateId: TemplateId) => templateId,
    (project, templateId): ProjectTemplate =>
      project.data[templateId]?.template as ProjectTemplate,
  );

export const useActiveTemplateId = (): TemplateId => {
  return useSelector(selectActiveTemplateId);
};

// NOTE: cast above is a known type lie — the return can be undefined when
// the template hasn't loaded yet or activeId points at a deleted template.
// 23+ consumers currently treat the result as always-defined; tightening the
// type to `ProjectTemplate | undefined` is a separate cleanup PR (would
// surface TS errors across all callers and require auditing each one).
// For now consumers must defensively guard with `if (!template) return null`
// before reading fields off the result.
export const useActiveProjectTemplate = (): ProjectTemplate => {
  const activeId = useActiveTemplateId();
  const templateObject = useMemo(getActiveProjectTemplate, []);

  return useSelector((state: RootState) => templateObject(state, activeId));
};

export const useActiveProjectTemplateStore = (): ProjectTemplateStoreRecord => {
  const activeId = useActiveTemplateId();

  return useSelector(
    (state: RootState) => state.templates.project.data[activeId],
  );
};
